import * as React from 'react';
import isEqual from 'lodash/isEqual';
import BulkHours from './BulkHours';
import DayRow from './DayRow';
import { EditData, ViewData } from '@library/data-section';
import { useStoreContext } from '@/state';
import * as schedule from './schedule';

type Props = {
  userEditingStrategy: 'update' | 'suggest' | 'none';
  hours: schedule.Hours;
};

export default function ListingSchedule(props: Props) {
  const { analytics } = useStoreContext();
  const [saved, setSaved] = React.useState(false);
  const initial = { ...schedule.EMPTY_HOURS, ...(props.hours || {}) };

  const [hours, setHours] = React.useState<schedule.Hours>(initial);
  const [savedHours, setSavedHours] = React.useState<schedule.Hours>(initial);
  const dirty = !isEqual(hours, savedHours);

  const readOnly = props.userEditingStrategy === 'none';
  const known = schedule.hasAnyHours(hours);

  const header = (
    <div className="editor__listing__hours__card__header">
      <div>
        <h4 className="editor__listing__hours__card__title" id="Hours">
          {'Regular hours'}
        </h4>
        {!known && (
          <div className="editor__listing__hours__card__subtitle">
            {'No hours set for this listing'}
          </div>
        )}
      </div>
    </div>
  );

  if (readOnly) {
    return (
      <ViewData
        cy="hours-view"
        sectionTitle="Hours"
        userEditingStrategy={props.userEditingStrategy}
        show
        onClick={() => {}}
      >
        <section
          className="editor__listing__hours__card"
          aria-labelledby="Hours"
          data-cy="hours"
        >
          {header}
          <div className="editor__hours" data-cy="hours-summary">
            {schedule.DAYS.map((day) => {
              const spans = schedule.spansFor(savedHours, day.key);

              return (
                <div className="editor__hours__day" key={day.key}>
                  <span className="editor__hours__day__pill">{day.name}</span>
                  <span className="editor__hours__day__note">
                    {spans.length > 0
                      ? spans.map(schedule.formatSpan).join(', ')
                      : known ? 'Closed all day' : 'Not set'}
                  </span>
                </div>
              );
            })}
          </div>
        </section>
      </ViewData>
    );
  }

  return (
    <EditData
      cy="hours-edit"
      sectionTitle="Hours"
      analytics={analytics}
      userEditingStrategy={props.userEditingStrategy}
      show
      disable={!dirty}
      disableCancel={!dirty}
      cancelLabel="Discard changes"
      params={{ listing: { hours: { '<default>': hours } } }}
      cancel={() => setHours(savedHours)}
      onSuccess={() => {
        setSavedHours(hours);
        setSaved(true);
      }}
    >
      <section
        className="editor__listing__hours__card"
        aria-labelledby="Hours"
        data-cy="hours"
      >
        {header}
        <BulkHours
          known={known}
          onApply={(keys, spans) =>
            setHours((h) => schedule.setDays(h, keys, spans))}
        />
        <div className="editor__hours">
          {schedule.DAYS.map((day) => (
            <DayRow
              key={day.key}
              name={day.name}
              known={known}
              spans={schedule.spansFor(hours, day.key)}
              onToggleDay={() => setHours((h) => schedule.toggleDay(h, day.key))}
              onToggle24={() => setHours((h) => schedule.toggle24(h, day.key))}
              onAddSpan={() => setHours((h) => schedule.addSpan(h, day.key))}
              onUpdateSpan={(index, span) =>
                setHours((h) => schedule.updateSpan(h, day.key, index, span))}
              onRemoveSpan={(index) =>
                setHours((h) => schedule.removeSpan(h, day.key, index))}
            />
          ))}
        </div>
      </section>
      <div className="editor__listing__hours__save-notice" role="status">
        {dirty ? 'Unsaved changes' : saved && 'Saved Successfully!'}
      </div>
    </EditData>
  );
}
